import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories, mainSiteUrl } from '@/mocks/categories';

const languages = [
  { code: 'en', label: 'EN' },
  { code: 'pl', label: 'PL' },
  { code: 'es', label: 'ES' },
  { code: 'fr', label: 'FR' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const changeLang = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  const currentLang = languages.find((l) => l.code === (i18n.language || 'en').split('-')[0]) || languages[0];

  const isHome = location.pathname === '/';
  // On non-home pages the header sits on a light background, so it must always be solid.
  const solid = scrolled || !isHome;

  const navLinks = [
    { to: isHome ? '#categories' : '/#categories', label: t('nav.shoppingGuide'), anchor: true },
    { to: '/guides/usfans-spreadsheet-guide', label: t('nav.guides'), anchor: false },
    { to: isHome ? '#about' : '/#about', label: t('nav.about'), anchor: true },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid ? 'bg-background-50/95 border-b border-background-200 shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="mx-auto max-w-7xl h-16 flex items-center justify-between gap-4">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${solid ? 'bg-primary-500 text-background-50' : 'bg-background-50/90 text-primary-600'}`}>
              <i className="ri-grid-line text-lg" />
            </span>
            <span className={`font-heading font-semibold text-lg leading-none ${solid ? 'text-foreground-950' : 'text-background-50'}`}>
              USFans <span className={solid ? 'text-primary-600' : 'text-accent-400'}>Spreadsheet</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-semibold rounded-md whitespace-nowrap transition-colors ${
                solid ? 'text-foreground-800 hover:bg-background-100' : 'text-background-50 hover:bg-background-50/10'
              }`}
            >
              {t('nav.home')}
            </Link>
            {navLinks.map((link) =>
              link.anchor ? (
                <a
                  key={link.to}
                  href={link.to}
                  className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors ${
                    solid ? 'text-foreground-700 hover:bg-background-100' : 'text-background-50/90 hover:bg-background-50/10'
                  }`}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors ${
                    solid ? 'text-foreground-700 hover:bg-background-100' : 'text-background-50/90 hover:bg-background-50/10'
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-2">
            {/* Language switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen((v) => !v)}
                className={`flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-semibold whitespace-nowrap transition-colors ${
                  solid ? 'bg-background-100 text-foreground-800 hover:bg-background-200' : 'bg-background-50/15 text-background-50 hover:bg-background-50/25'
                }`}
                aria-label="Select language"
              >
                <i className="ri-global-line text-base" />
                <span>{currentLang.label}</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-32 bg-background-50 border border-background-200 rounded-lg p-1 shadow-sm">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLang(lang.code)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm whitespace-nowrap transition-colors ${
                        lang.code === currentLang.code
                          ? 'bg-primary-100 text-primary-700 font-semibold'
                          : 'text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      {lang.label}
                      {lang.code === currentLang.code && <i className="ri-check-line text-base" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* CTA */}
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex h-9 items-center px-4 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              {t('nav.buyNow')}
            </a>

            {/* Mobile toggle */}
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-md ${
                solid ? 'text-foreground-800 bg-background-100' : 'text-background-50 bg-background-50/15'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200 px-4 py-4 max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col gap-1">
            <Link to="/" className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-semibold text-foreground-900 hover:bg-background-100">
              <i className="ri-home-4-line text-base text-primary-600" />
              {t('nav.home')}
            </Link>
            {navLinks.map((link) =>
              link.anchor ? (
                <a key={link.to} href={link.to} onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100">
                  <i className="ri-arrow-right-s-line text-base text-foreground-400" />
                  {link.label}
                </a>
              ) : (
                <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100">
                  <i className="ri-arrow-right-s-line text-base text-foreground-400" />
                  {link.label}
                </Link>
              )
            )}

            {/* Categories in mobile menu */}
            <p className="mt-3 px-3 text-xs font-bold uppercase tracking-wider text-foreground-500">{t('nav.categories')}</p>
            <div className="grid grid-cols-2 gap-1 mt-1">
              {categories.map((cat) => (
                <a
                  key={cat.key}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-2.5 py-2 rounded-md text-xs font-medium text-foreground-800 hover:bg-background-100 whitespace-nowrap"
                >
                  <span className="w-6 h-6 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700 shrink-0">
                    <i className={`${cat.icon} text-xs`} />
                  </span>
                  {t(cat.key)}
                </a>
              ))}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="mt-3 px-3 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold text-center whitespace-nowrap"
            >
              {t('nav.buyNow')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}